import { LightningElement, api, wire } from "lwc";

import { getClient } from "@devforce/trailhead-client-api";
import { logger } from "@devforce/trailhead-client-logger";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { label } from "common/labels";

import { USER_ORGS_ROUTE, MAX_ORG_TOTAL_COUNT } from "./constants";
import * as locales from "./locales";
import { Org } from "./types";
import { BooleanAttr, attrToBool, exists } from "common/helpers";
import { trackUserActivity } from "common/track";

const SESSION_STORAGE_ERROR = "Could not access session storage.";

// Polling request settings
const COMPLETED_POLLING_INTERVAL = 10000;
const COMPLETED_POLLING_RETRIES = 120;
const COMPLETED_PROGRESS_FACTOR = 1.48;
const COMPLETED_PROGRESS_MIN = 10;
const COMPLETED_PROGRESS_MAX = 74;

const REACHABLE_POLLING_INTERVAL = 5000;
const REACHABLE_POLLING_RETRIES = 36;
const REACHABLE_PROGRESS_FACTOR = 0.72;

export function getDefaultOrg(orgs: Array<Org>, unitApiName?: string) {
  if (!orgs || !orgs.length) {
    return null;
  }

  // Always show pending orgs primarily
  const pending = orgs.find((o) => o.signup_request_status === "pending");

  if (pending) {
    return pending;
  }

  if (unitApiName) {
    // Then, most recently last_used_for org
    const used = orgs
      .filter(
        (o) =>
          !o.expired &&
          o.last_used_for &&
          o.last_used_for.unit_api_name === unitApiName
      )
      .sort((a, b) =>
        new Date(a.last_used_for!.used_on) < new Date(b.last_used_for!.used_on)
          ? 1
          : -1
      )[0];

    if (used) {
      return used;
    }
  }

  // Otherwise, find the best available
  return (
    orgs.find((o) => o.active_org) || orgs.filter((o) => !o.expired)[0] || null
  );
}

export default class extends LightningElement {
  @api moduleApiName: string | undefined = undefined;
  @api unitApiName: string | undefined = undefined;
  @api unitUid: string | undefined = undefined;
  @api isCustomDeOrg: BooleanAttr = false;
  @api templateUri: string | undefined = undefined;
  @api autoswitch: "off" | "on" = "on";
  @api label: string | null = null;

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;

  private get shouldAutoSwitch() {
    return this.autoswitch !== "off";
  }
  private get showJitComponent() {
    // The user has no orgs
    return !this.orgsAvailable;
  }

  private isOpen: boolean = false;
  private isLoading: boolean = true;
  private loadError: string | null = null;
  private runtimeError: string | null = null;
  private showCreateForm: boolean = false;
  private createProgress: number = COMPLETED_PROGRESS_MIN;
  private selected: Org | null = null;
  private _orgs: Array<Org> = [];
  private orgsAvailable: boolean = false;
  private name: string | null = null;

  private get orgs() {
    return this.decorateOrgs(this._orgs);
  }

  private set orgs(orgs: Array<Org>) {
    this._orgs = orgs;
  }

  private fromTrailhead = getClient();

  connectedCallback() {
    this.load().catch((e: Error) => logger.warn(e.message));
  }

  private get isCreating() {
    return (
      !this.loadError &&
      !this.runtimeError &&
      !!this.selected &&
      this.selected.signup_request_status == "pending"
    );
  }

  private get launchPath() {
    if (!this.selected) {
      return undefined;
    }

    return this.selected.launch_de_path || this.selected.instance_url;
  }

  private get isLaunchDisabled() {
    return !!(
      this.isLoading ||
      !this.launchPath ||
      (this.selected &&
        // Not pending, disconnected, or expired
        (this.selected.signup_request_status || "") === "pending" &&
        !this.selected.expired)
    );
  }

  private get isCreateDisabled() {
    return (
      !!this.orgs.filter((o) => o.signup_request_status === "pending").length ||
      this.orgs.filter((o) => o.auto_created_org).length >= MAX_ORG_TOTAL_COUNT
    );
  }

  private get orgPickerErrorId() {
    return this.runtimeError ? "orgPickerError" : null;
  }

  private async load() {
    try {
      await this.getOrgs();
      this.dispatchEvent(new CustomEvent("load", { detail: this.selected }));
    } catch (error) {
      this.loadError = this.labels.loadFailed;
      this.handleError(error as Error);
    }

    try {
      // Indicate if an org is already being created
      if (this.selected && this.selected.signup_request_status === "pending") {
        this.setInitialCreateProgress(this.selected);
        const reachableOrg = await this.orgReachability(this.selected);
        this.handleOrgCreated(reachableOrg);
      }
    } catch (error) {
      this.handleOrgCreateError(error as Error);
    }
  }

  private decorateOrgs(orgs: Array<Org>) {
    if (!orgs || !orgs.length) {
      return [];
    }

    return orgs
      .map((o) => ({
        ...o,
        selected: this.selected ? o.id === this.selected.id : false,
      }))
      .sort(
        (a, b) =>
          // Most recently created first
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }

  private async setActive(org: Org | null = null) {
    if (!org || org.signup_request_status === "pending") return;

    try {
      await this.fromTrailhead.post(`${USER_ORGS_ROUTE}/active`, {
        id: org.id,
      });
      this.runtimeError = null;
    } catch (error) {
      this.runtimeError = this.labels.setActiveFailed;
      this.handleError(error as Error);

      // Return focus back to org picker button
      const orgButton = this.template.querySelector("#orgButton");
      if (orgButton) {
        orgButton.focus();
      }
    }
  }

  private async getOrgs() {
    this.isLoading = true;
    this.loadError = null;

    const user_orgs_route = attrToBool(this.isCustomDeOrg)
      ? `${USER_ORGS_ROUTE}?unit_uid=${this.unitUid}`
      : USER_ORGS_ROUTE;

    const { data } = await this.fromTrailhead.get<{ data: Org[] }>(
      user_orgs_route
    );

    this.selected = getDefaultOrg(
      data,
      this.shouldAutoSwitch ? this.unitApiName : undefined
    );
    this.orgs = data;

    if (this.selected) {
      // Ensure the selected org is set as the active_org
      if (!this.selected.active_org) {
        await this.setActive(this.selected);
      }
      this.updateOrgsAvailability(this.selected, this.orgs);
    }

    this.isLoading = false;
  }

  private updateOrgsAvailability(org: Org, orgs: Org[]) {
    // If the org is no longer pending or there are already orgs available
    if (org.signup_request_status != "pending" || orgs.length > 1) {
      this.orgsAvailable = true;
    }
  }

  private setInitialCreateProgress(org: Org) {
    try {
      const val = sessionStorage.getItem(`th-org-create-progress-${org.id}`);

      if (val) {
        this.createProgress = parseInt(val);
      }
    } catch (error) {
      logger.warn(SESSION_STORAGE_ERROR);
    }
  }

  private updateCreateProgress(org: Org, newValue: number) {
    if (this.showJitComponent) return;

    // Prevent showing 100% if still checking
    this.createProgress = Math.min(newValue, 95);

    try {
      sessionStorage.setItem(
        `th-org-create-progress-${org.id}`,
        this.createProgress.toString()
      );
    } catch (error) {
      logger.warn(SESSION_STORAGE_ERROR);
    }
  }

  private clearCreateProgress(org: Org) {
    this.createProgress = 0;

    try {
      sessionStorage.removeItem(`th-org-create-progress-${org.id}`);
    } catch (error) {
      logger.warn(SESSION_STORAGE_ERROR);
    }
  }

  private async orgReachability(org: Org): Promise<Org> {
    // Poll for "completed" status
    const { data: completedOrg } = await this.fromTrailhead.poll<{
      data: Org;
    }>({
      request: () => this.fromTrailhead.get(`${USER_ORGS_ROUTE}/${org.id}`),
      done: ({ data }: { data: Org }) => {
        if (data.signup_request_status === "failed") {
          throw new Error("signup_request Failed");
        }

        this.updateCreateProgress(
          data,
          // Limit the progress at this step so that "reachable" checks also show progress
          Math.min(
            // Ensure progress shows at least the minimum
            Math.max(this.createProgress, COMPLETED_PROGRESS_MIN) +
              COMPLETED_PROGRESS_FACTOR,
            COMPLETED_PROGRESS_MAX
          )
        );

        return data.signup_request_status === "completed";
      },
      interval: COMPLETED_POLLING_INTERVAL,
      retries: COMPLETED_POLLING_RETRIES,
    });

    this.updateCreateProgress(completedOrg, COMPLETED_PROGRESS_MAX);

    // Poll for org reachability
    await this.fromTrailhead.poll({
      request: () =>
        isOrgReachable(completedOrg, attrToBool(this.isCustomDeOrg)),
      done: (isReachable: boolean) => {
        this.updateCreateProgress(
          completedOrg,
          this.createProgress + REACHABLE_PROGRESS_FACTOR
        );

        return isReachable;
      },
      interval: REACHABLE_POLLING_INTERVAL,
      retries: REACHABLE_POLLING_RETRIES,
    });

    return completedOrg;
  }

  private async handleShowCreateFrom() {
    this.showCreateForm = true;
  }

  private async getCustomDeOrgName() {
    try {
      const { data } = await this.fromTrailhead.get<{ data: string }>(
        `${USER_ORGS_ROUTE}/random_name?unit_uid=${this.unitUid}`
      );
      this.name = data;
    } catch (error) {
      this.name = label("defaultPlaygroundName");
    }
  }

  // when user has no TPO created yet, and is creating the first one on unit page
  private async handleCreateJIT(e?: { detail: { name: string } }) {
    trackUserActivity(this, "tpo_create_clicked", {
      module_api_name: this.moduleApiName ?? "",
      unit_api_name: this.unitApiName ?? "",
      pageLocation: "th-org-picker-modal",
    });
    // the first TPO to be created happens to be a custom DE org
    if (attrToBool(this.isCustomDeOrg)) {
      await this.getCustomDeOrgName();
    }
    // pass button click event as argument
    await this.handleCreate(e);
  }

  private async handleCreate(e?: { detail: { name: string } }) {
    try {
      // Start a creation attempt
      let payload: { name: string | null; unit_uid?: string } | undefined;
      // scenario 1: when user has no TPO created yet, and is creating the first one on unit page. It is a click event on Create Playground button
      // scenario 2: when user has >= 1 TPO(s) created already, and is creating a new one on unit page. It is a submit event on orgCreateModal
      if (e) {
        if (!e.detail.name) {
          // scenario 1: click event
          if (attrToBool(this.isCustomDeOrg)) {
            payload = { name: this.name, unit_uid: this.unitUid };
          }
        } else {
          // scenario 2: submit event
          payload = { name: e.detail.name };
          if (attrToBool(this.isCustomDeOrg)) {
            payload.unit_uid = this.unitUid;
          }
        }
      }
      const { data: pendingOrg } = await this.fromTrailhead.post<{ data: Org }>(
        USER_ORGS_ROUTE,
        payload
      );

      this.dispatchEvent(
        new CustomEvent("createstart", { detail: pendingOrg })
      );

      // Update state
      this.selected = pendingOrg;
      this.orgs = this.orgs.concat(pendingOrg);
      this.isLoading = false;

      // HACK: dynamically update live region after the
      // render so that screen readers announce the content
      exists(".creating__label", this.template)
        .then((node) => {
          if (node) {
            node.innerHTML = this.labels.createProgress;
          }
        })
        .catch(() => undefined);

      // Close create modal unless using JIT Component
      if (!this.showJitComponent) {
        this.handleRequestClose();
      }

      // Await reachability
      const reachableOrg = await this.orgReachability(pendingOrg);

      this.handleOrgCreated(reachableOrg);
    } catch (error) {
      this.handleOrgCreateError(error as Error);
    }
  }

  private handleOrgCreated(org: Org) {
    // Ensure the selected org has fresh data if still selected
    if (this.selected && this.selected.id === org.id) {
      this.selected = org;
      this.setActive(org).catch((e: Error) => logger.warn(e.message));
    }

    // Update org data
    this.orgs = this.orgs.map((o) => (o.id === org.id ? org : o));

    this.clearCreateProgress(org);

    // Hide JIT Component
    this.orgsAvailable = true;

    this.dispatchEvent(new CustomEvent("createend", { detail: org }));
  }

  private handleOrgCreateError(error: Error) {
    this.selected = getDefaultOrg(
      this.orgs,
      this.shouldAutoSwitch ? this.unitApiName : undefined
    );
    this.runtimeError = this.labels.jitCreateFailed;
    this.handleError(error);
  }

  private handleSelectOption(e: CustomEvent) {
    const id = e.detail;
    this.selected = this.orgs.find((o) => o.id === id) || null;
    this.setActive(this.selected).catch((e: Error) => logger.warn(e.message));
    this.isOpen = false;
    this.dispatchEvent(new CustomEvent("change", { detail: this.selected }));
  }

  private async handleDeleteOption(e: CustomEvent) {
    this.orgs = this.orgs.filter((o) => o.id !== e.detail);
  }

  private handleRequestOpen() {
    if (this.isLoading || this.loadError) {
      return;
    }

    this.isOpen = true;
  }

  private handleRequestClose() {
    this.showCreateForm = false;
    this.isOpen = false;

    // Explictly re-focus the button becuase switching between two
    // focus-traps (modals) causes the intedend target to become lost.
    this.template.querySelector("button.selected")!.focus();
  }

  private handleError(e: CustomEvent | Error = new Error()) {
    const error: Error = e instanceof CustomEvent ? e.detail : e;
    this.dispatchEvent(new CustomEvent("error", { detail: error.message }));
    logger.error(error.message);
  }

  private handleLaunch() {
    trackUserActivity(this, "tpo_launch_clicked", {
      module_api_name: this.moduleApiName ?? "",
      unit_api_name: this.unitApiName ?? "",
      playground_info: {
        playground_type: this.selected!.type,
        template: attrToBool(this.isCustomDeOrg)
          ? {
              name: this.templateUri ?? "",
              provider: this.selected!.provider,
            }
          : undefined,
      },
      pageLocation: "th-org-picker-modal",
    });
  }
}

/**
 * Determine if an org is reachable (after creation) by
 * requesting open endpoints. The org is reachable if the
 * user's client resolves the freshly propagated DNS entries.
 */
export async function isOrgReachable(
  org: Org,
  isCustomDeOrg: boolean
): Promise<boolean> {
  const instance_url = org.instance_url;

  // scenario 1: if custom DE org
  // Ping instance url for response statuses
  if (isCustomDeOrg) {
    return await fetch(`${instance_url}/.well-known/openid-configuration`)
      .then((response) => {
        return response.status === 200;
      })
      .catch(() => {
        return false;
      });
  }

  // scenario 2: if regular TPO
  const subdomain = instance_url.split(".")[0];

  // If all response statuses are 200, 302, or 0, then the playground is accessible
  let determineAccessibleDomains = (
    responses: Response[] | { status: number }[]
  ) => {
    const results = responses.every(
      (r) => r.status === 200 || r.status === 302 || r.status === 0
    );

    if (results) {
      return Promise.resolve({ accessible: true });
    } else {
      return Promise.resolve({ accessible: false });
    }
  };

  // Ping legacy org domains for response statuses
  let orgDomains = () =>
    Promise.all([
      fetch(`${subdomain}.my.salesforce.com/.well-known/openid-configuration`),
      fetch(
        `${subdomain}--c.documentforce.com/.well-known/openid-configuration`,
        {
          mode: "no-cors",
        }
      ),
      fetch(
        `${subdomain}--th-con-app.visualforce.com/.well-known/openid-configuration`,
        {
          mode: "no-cors",
        }
      ),
      fetch(
        `${subdomain}.lightning.force.com/.well-known/openid-configuration`,
        {
          mode: "no-cors",
        }
      ),
    ])
      .then((responses) => {
        return responses;
      })
      .catch(() => {
        return Promise.resolve([{ status: 404 }]);
      });

  // Ping enhanced org domains for response statuses
  let enhancedOrgDomains = () =>
    Promise.all([
      fetch(`${subdomain}.my.salesforce.com/.well-known/openid-configuration`),
      fetch(`${subdomain}.file.force.com/.well-known/openid-configuration`, {
        mode: "no-cors",
      }),
      fetch(
        `${subdomain}.lightning.force.com/.well-known/openid-configuration`,
        {
          mode: "no-cors",
        }
      ),
      fetch(
        `${subdomain}--th-con-app.vf.force.com/.well-known/openid-configuration`,
        {
          mode: "no-cors",
        }
      ),
    ])
      .then((responses) => {
        return responses;
      })
      .catch(() => {
        return Promise.resolve([{ status: 404 }]);
      });

  // Ping enhanced sharded org domains for response statuses
  let shardedEnhancedOrgDomains = () =>
    Promise.all([
      fetch(
        `${subdomain}.trailblaze.my.salesforce.com/.well-known/openid-configuration`
      ),
      fetch(
        `${subdomain}--th-con-app.trailblaze.container.force.com/.well-known/openid-configuration`,
        {
          mode: "no-cors",
        }
      ),
    ])
      .then((responses) => {
        return responses;
      })
      .catch(() => {
        return Promise.resolve([{ status: 404 }]);
      });

  let accessibleOrgDomains = orgDomains().then((responses) => {
    return determineAccessibleDomains(responses);
  });

  let accessibleEnhancedOrgDomains = enhancedOrgDomains().then((responses) => {
    return determineAccessibleDomains(responses);
  });

  let accessibleShardedEnhancedOrgDomains = shardedEnhancedOrgDomains().then(
    (responses) => {
      return determineAccessibleDomains(responses);
    }
  );

  let results = await Promise.all([
    accessibleOrgDomains,
    accessibleEnhancedOrgDomains,
    accessibleShardedEnhancedOrgDomains,
  ]);

  return results.some((r) => r.accessible);
}
